import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Info, RefreshCw, SearchX, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import FaqSection from '../components/common/FaqSection';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import IssueHero from '../components/issue-subscription/IssueHero';
import IssueKeyStats from '../components/issue-subscription/IssueKeyStats';
import IssueTimeline from '../components/issue-subscription/IssueTimeline';
import SubscriptionBook from '../components/issue-subscription/SubscriptionBook';
import QuotaAllocation from '../components/issue-subscription/QuotaAllocation';
import LotCalculator from '../components/issue-subscription/LotCalculator';
import AnchorBook from '../components/issue-subscription/AnchorBook';
import IssueObjects from '../components/issue-subscription/IssueObjects';
import IssueFundamentals from '../components/issue-subscription/IssueFundamentals';
import IssueOperations from '../components/issue-subscription/IssueOperations';
import AsbaBidCard from '../components/issue-subscription/AsbaBidCard';
import { BrokerConsensusCard, GmpCard, RegistrarCard } from '../components/issue-subscription/IssueSideCards';
import {
  anchorSchedule,
  biddingDay,
  buildIssueFaqs,
  buildIssueSchedule,
  buildIssuerProfile,
  buildLotSlabs,
  buildQuotaAllocation,
  describeBiddingWindow,
  issuePhase,
  mapBidBook,
  resolveIssue,
  summarizeBrokerReviews,
} from '../utils/issueSubscription';
import { formatLongDate } from '../utils/gmpBoard';
import { buildIcsCalendar, todayIso } from '../utils/ipoCalendar';
import { matchKey } from '../utils/smeIpos';
import { fetchIssueSubscription, fetchIssuerProfile } from '../store/slices/issueSubscriptionSlice';
import {
  fetchSmeSubscriptionSummary,
  fetchSubscriptionSummary,
  selectSmeSubscriptionRows,
  selectSubscriptionRows,
} from '../store/slices/subscriptionSlice';
import { fetchSmeUpcomingIpos, fetchUpcomingIpos, selectOpeningSoonIpos, selectSmeOpeningSoonIpos } from '../store/slices/upcomingIposSlice';
import { fetchGmp, selectGmpBoardRows } from '../store/slices/gmpSlice';
import { fetchSmeGmp, selectSmeGmpBoardRows } from '../store/slices/smeGmpSlice';
import { fetchLiveIpos } from '../store/slices/ipoSlice';

const NOTICE_MS = 5000;
const FEED_MAX_AGE_MS = 60000;
const PROFILE_MAX_AGE_MS = 10 * 60 * 1000;
// The live bid book is re-polled while bidding is open
const BOOK_POLL_MS = 2 * 60 * 1000;
const CLOCK_TICK_MS = 30000;

const AS_OF = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const isSettled = (status) => status === 'succeeded' || status === 'failed';

export const IssueSubscriptionPage = () => {
  const dispatch = useDispatch();
  const { symbol: rawSymbol = '' } = useParams();
  const symbol = rawSymbol.trim().toUpperCase();
  const today = useMemo(() => todayIso(), []);
  const [now, setNow] = useState(() => new Date());
  const [notice, setNotice] = useState(null);
  const asbaRef = useRef(null);
  const anchorRef = useRef(null);

  // GET /api/market/subscription/{symbol} is the bid book; the other feeds
  // identify the issue (company, band, lot, dates, GMP)
  const bookEntry = useSelector((state) => state.issueSubscription.bySymbol[symbol]);
  const subscriptionRows = useSelector(selectSubscriptionRows);
  const smeSubscriptionRows = useSelector(selectSmeSubscriptionRows);
  const scheduleRows = useSelector(selectOpeningSoonIpos);
  const smeScheduleRows = useSelector(selectSmeOpeningSoonIpos);
  const gmpRows = useSelector(selectGmpBoardRows);
  const smeGmpRows = useSelector(selectSmeGmpBoardRows);
  const liveCurrent = useSelector((state) => state.ipo.liveIposCurrent);
  const liveUpcoming = useSelector((state) => state.ipo.liveIposUpcoming);
  const liveLoaded = useSelector((state) => Boolean(state.ipo.liveIposRefreshedAt || state.ipo.liveIposError));
  const liveLoading = useSelector((state) => state.ipo.liveIposLoading);
  const feedsSettled = useSelector(
    (state) =>
      isSettled(state.subscription.status) &&
      isSettled(state.smeSubscription.status) &&
      isSettled(state.upcomingIpos.status) &&
      isSettled(state.smeUpcomingIpos.status)
  );

  const loadFeeds = (force = false) => {
    const options = force ? {} : { maxAgeMs: FEED_MAX_AGE_MS };
    dispatch(fetchIssueSubscription({ symbol, ...options }));
    dispatch(fetchSubscriptionSummary(options));
    dispatch(fetchSmeSubscriptionSummary(options));
    dispatch(fetchUpcomingIpos(options));
    dispatch(fetchSmeUpcomingIpos(options));
    dispatch(fetchGmp(options));
    dispatch(fetchSmeGmp(options));
    if (force || (!liveLoaded && !liveLoading)) dispatch(fetchLiveIpos(false));
  };

  useEffect(() => {
    if (symbol) loadFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, symbol]);

  const resolved = useMemo(
    () =>
      resolveIssue({
        symbol,
        subscriptionRows: [...subscriptionRows, ...smeSubscriptionRows],
        liveItems: [...liveCurrent, ...liveUpcoming],
        scheduleRows: [...scheduleRows, ...smeScheduleRows],
        gmpRows: [...gmpRows, ...smeGmpRows],
      }),
    [symbol, subscriptionRows, smeSubscriptionRows, liveCurrent, liveUpcoming, scheduleRows, smeScheduleRows, gmpRows, smeGmpRows]
  );

  const rawBook = useMemo(() => mapBidBook(bookEntry?.rows), [bookEntry]);
  // A symbol no schedule lists any more can still have a bid book
  const issue = useMemo(
    () =>
      resolved ||
      (rawBook.hasBids
        ? { symbol, company: symbol, board: 'mainboard', series: 'EQ', openOn: null, closeOn: null, boaOn: null, listingOn: null, priceLow: null, priceHigh: null, lotSize: null, minInvestment: null, sizeCr: null, gmp: null }
        : null),
    [resolved, rawBook, symbol]
  );

  const company = resolved?.company || null;
  useEffect(() => {
    if (company) dispatch(fetchIssuerProfile({ company, maxAgeMs: PROFILE_MAX_AGE_MS }));
  }, [dispatch, company]);
  const profileEntry = useSelector((state) => (company ? state.issueSubscription.profiles[matchKey(company)] : null));

  const profile = useMemo(() => buildIssuerProfile(profileEntry?.detail), [profileEntry]);
  const brokerSummary = useMemo(() => summarizeBrokerReviews(profileEntry?.reviews), [profileEntry]);
  const book = useMemo(
    () => mapBidBook(bookEntry?.rows, { priceHigh: issue?.priceHigh, board: issue?.board }),
    [bookEntry, issue]
  );

  const phase = issue ? issuePhase(issue, today) : 'unknown';
  const isLive = phase === 'open' || phase === 'closing_today';
  const dayNumber = issue ? biddingDay(issue, today) : null;

  const listingVenue = issue?.board === 'sme' ? 'NSE Emerge' : /bse/i.test(profileEntry?.detail?.exchanges || '') ? 'BSE & NSE' : 'NSE';
  const schedule = useMemo(
    () =>
      issue
        ? buildIssueSchedule(
          { ...issue, registrar: profile?.registrar?.name.split(' ')[0] || null, listingVenue },
          today
        )
        : [],
    [issue, profile, listingVenue, today]
  );
  const allotmentStep = schedule.find((step) => step.key === 'allotment') || {};
  const anchor = anchorSchedule({ openOn: issue?.openOn, allotmentOn: allotmentStep.date });
  const slabs = useMemo(
    () => (issue ? buildLotSlabs({ lotSize: issue.lotSize, price: issue.priceHigh, board: issue.board }) : []),
    [issue]
  );
  const allocation = useMemo(() => buildQuotaAllocation(book, issue?.priceHigh), [book, issue]);
  const faqs = useMemo(
    () => (issue ? buildIssueFaqs({ issue, slabs, schedule, profile, formatDate: formatLongDate }) : []),
    [issue, slabs, schedule, profile]
  );
  const detailPath = profileEntry?.recordId
    ? `${issue?.board === 'sme' ? '/smeipo' : '/mainboardipo'}/${profileEntry.recordId}`
    : null;
  const listPath = issue?.board === 'sme' ? '/sme-ipo-subscription' : '/ipo-subscription';
  const asOf = bookEntry?.refreshedAt && book.hasBids ? `${AS_OF.format(new Date(bookEntry.refreshedAt))} IST` : null;
  const priceBand =
    issue?.priceHigh && issue.priceLow !== issue.priceHigh ? `₹${issue.priceLow} – ₹${issue.priceHigh}` : issue?.priceHigh ? `₹${issue.priceHigh}` : null;

  // Countdown clock, and a fresh bid book every couple of minutes while bidding is open
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!isLive || !symbol) return undefined;
    const timer = setInterval(() => dispatch(fetchIssueSubscription({ symbol, maxAgeMs: BOOK_POLL_MS - 5000 })), BOOK_POLL_MS);
    return () => clearInterval(timer);
  }, [dispatch, isLive, symbol]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = issue
      ? `${issue.company} IPO Subscription Status (${issue.symbol}) | Alambrix IPO`
      : `${symbol} IPO Subscription | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [issue, symbol]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleAddToCalendar = () => {
    const calendarIssue = { ...issue, id: issue.symbol, registrar: profile?.registrar?.name };
    const events = [
      issue.openOn && issue.closeOn && { id: `${issue.symbol}-open`, type: 'open', date: issue.openOn, issue: calendarIssue },
      issue.closeOn && issue.openOn && { id: `${issue.symbol}-close`, type: 'close', date: issue.closeOn, issue: calendarIssue },
      allotmentStep.date && { id: `${issue.symbol}-allotment`, type: 'allotment', date: allotmentStep.date, issue: calendarIssue },
    ].filter(Boolean);
    if (!events.length) return;
    const blob = new Blob([buildIcsCalendar(events)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${issue.symbol.toLowerCase()}-ipo.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const containerClass =
    'w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16';

  if (!issue) {
    const stillLoading = !symbol ? false : !feedsSettled || !liveLoaded || !bookEntry || bookEntry.status === 'loading';
    return (
      <>
        <TickerTape />
        <main className="flex-1 bg-slate-50/70">
          <div className={containerClass}>
            {stillLoading ? (
              <div className="space-y-4 animate-pulse" aria-label="Loading issue details">
                <div className="h-9 rounded-xl bg-slate-200/70" />
                <div className="h-28 rounded-2xl bg-white border border-slate-200" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-24 rounded-2xl bg-white border border-slate-200" />
                  ))}
                </div>
                <div className="h-72 rounded-2xl bg-white border border-slate-200" />
              </div>
            ) : (
              <section className="max-w-xl mx-auto mt-10 bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
                <SearchX className="w-10 h-10 mx-auto text-slate-300" aria-hidden="true" />
                <h1 className="mt-3 text-lg font-extrabold text-slate-900">No IPO found for “{symbol || rawSymbol}”</h1>
                <p className="mt-2 text-sm text-slate-500">
                  This symbol isn’t in the current exchange schedules and has no bid data. It may have listed already, or the
                  symbol may be different.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <Link to="/ipo-subscription" className="px-4 py-2 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white">
                    Live IPO Subscription
                  </Link>
                  <Link to="/sme-ipo-subscription" className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50">
                    SME IPO Subscription
                  </Link>
                  <button
                    type="button"
                    onClick={() => loadFeeds(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Try again
                  </button>
                </div>
              </section>
            )}
          </div>
        </main>
      </>
    );
  }

  const shortName = issue.company.replace(/\s+(Ltd\.?|Limited)$/i, '');

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className={`${containerClass} space-y-5 sm:space-y-6`}>
          <IssueHero
            issue={issue}
            profile={profile}
            phase={phase}
            dayNumber={dayNumber}
            windowInfo={describeBiddingWindow(issue, now)}
            detailPath={detailPath}
            listPath={listPath}
            onApply={() => scrollTo(asbaRef)}
            onAnchor={() => scrollTo(anchorRef)}
          />

          <IssueKeyStats issue={issue} profile={profile} book={book} />

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 sm:gap-6 items-start">
            <div className="space-y-5 sm:space-y-6 min-w-0">
              <IssueTimeline schedule={schedule} onAddToCalendar={handleAddToCalendar} />

              <SubscriptionBook
                book={book}
                phase={phase}
                asOf={asOf}
                dayNumber={dayNumber}
                openOnLabel={issue.openOn ? formatLongDate(issue.openOn) : null}
                status={bookEntry?.status || 'idle'}
                error={bookEntry?.error}
                onRetry={() => dispatch(fetchIssueSubscription({ symbol }))}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                <QuotaAllocation
                  allocation={allocation}
                  board={issue.board}
                  anchorBidLabel={anchor.bidOn ? `${formatLongDate(anchor.bidOn)} (tentative)` : null}
                />
                <LotCalculator slabs={slabs} price={issue.priceHigh} board={issue.board} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                <AnchorBook ref={anchorRef} anchor={anchor} priceBand={priceBand} estimated={allotmentStep.estimated} />
                <IssueObjects objects={profile?.objects || []} detailPath={detailPath} />
              </div>

              <IssueFundamentals profile={profile} />

              <IssueOperations operations={profile?.operations} />
            </div>

            <aside className="space-y-5 sm:space-y-6 xl:sticky xl:top-20" aria-label="Bid and issue facts">
              {/* <AsbaBidCard key={issue.symbol} ref={asbaRef} issue={issue} slabs={slabs} phase={phase} onNotice={setNotice} /> */}
              <GmpCard gmp={issue.gmp} isSme={issue.board === 'sme'} />
              <BrokerConsensusCard summary={brokerSummary} isSme={issue.board === 'sme'} />
              <RegistrarCard profile={profile} company={issue.company} />
            </aside>
          </div>

          <FaqSection
            id="issue-faq"
            title={`Frequently Asked Questions: ${shortName} IPO`}
            subtitle="Price band, dates, how to apply and how the subscription figures work."
            faqs={faqs}
          />

          {/* Discussion section hidden for now:
          <GmpDiscussion
            initialThreads={[]}
            copy={{
              headingId: 'issue-discussion-heading',
              title: 'Investor Discourse & Subscription Debates',
              subtitle: `Discuss ${shortName}’s bidding trends, GMP and allotment odds with fellow investors.`,
              placeholder: `Post your bid thesis, allotment probability query, or analysis on ${shortName}...`,
              submitLabel: 'Post Perspective',
              startLabel: 'Start New Discussion',
            }}
          />
          */}

        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default IssueSubscriptionPage;
